/**
 * pendingConnectorCallback.ts — keeps a /connectors/callback address across a
 * sign-in detour, the way pendingInvite.ts keeps an invite: a signed-out
 * visitor (ProtectedRoute) or a session that expires while the callback page
 * is finishing (the api 401 handler sends the tab to /auth) comes back to
 * the callback after signing in, so the connection still completes.
 *
 * sessionStorage (this tab only) and a 10-minute life: the server's pending
 * connection expires after 10 minutes anyway (connectorContract
 * LIMITS.pendingTtlMs), and the address carries a one-time code.
 */

export const PENDING_CONNECTOR_CALLBACK_KEY = 'fd:pendingConnectorCallback';
const TTL_MS = 10 * 60 * 1000;
const CALLBACK_PATH = '/connectors/callback';

export function rememberPendingConnectorCallback(href: string, now: number = Date.now()): void {
  if (typeof href !== 'string' || !href.startsWith(`${CALLBACK_PATH}?`)) return;
  try {
    sessionStorage.setItem(PENDING_CONNECTOR_CALLBACK_KEY, JSON.stringify({ href, at: now }));
  } catch {
    /* storage unavailable: the user starts Connect again after signing in */
  }
}

export function clearPendingConnectorCallback(): void {
  try {
    sessionStorage.removeItem(PENDING_CONNECTOR_CALLBACK_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Reads and clears the stashed callback address; null when none or stale. */
export function consumePendingConnectorCallback(now: number = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_CONNECTOR_CALLBACK_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  clearPendingConnectorCallback();
  try {
    const { href, at } = JSON.parse(raw);
    if (typeof href !== 'string' || !href.startsWith(`${CALLBACK_PATH}?`)) return null;
    if (typeof at !== 'number' || now - at > TTL_MS || now < at) return null;
    return href;
  } catch {
    return null;
  }
}
